import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { trackVisit } from './analytics.js'
import { registerAdapter } from './stores/resourceRegistry.js'
import { datasetResourceAdapter } from './stores/resourceAdapters/datasetResourceAdapter.js'
import { fontResourceAdapter } from './stores/resourceAdapters/fontResourceAdapter.js'
import { imageResourceAdapter } from './stores/resourceAdapters/imageResourceAdapter.js'
import { audioResourceAdapter } from './stores/resourceAdapters/audioResourceAdapter.js'
import { model3dResourceAdapter } from './stores/resourceAdapters/model3dResourceAdapter.js'

import Button from './components/form/Button.vue'
import Input from './components/form/Input.vue'
import Textarea from './components/form/Textarea.vue'
import Label from './components/form/Label.vue'
import FormGroup from './components/form/FormGroup.vue'
import Select from './components/form/Select.vue'
import Checkbox from './components/form/Checkbox.vue'
import Position from './components/form/Position.vue'
import ProgressBar from './components/form/ProgressBar.vue'
import TabContainer from './components/form/TabContainer.vue'
import TabPane from './components/form/TabPane.vue'

import { ColorPicker } from "vue3-colorpicker";
import "vue3-colorpicker/style.css";

import './assets/fonts.css'
import './assets/material-icons.css'

/* import the fontawesome core */
import { library } from '@fortawesome/fontawesome-svg-core';
/* import font awesome icon component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
/* import specific icons */
import { faPlusCircle, faCircle, faTrashCan, faUserSecret, faHouse, faBolt, faExpand, faCompress, faEdit, faFont, faImage, faDatabase, faVolumeHigh, faWaveSquare, faRotateLeft, faCube } from '@fortawesome/free-solid-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

import {Tabs, Tab} from 'vue3-tabs-component';

// Preload icon fonts for Konva canvas rendering
async function preloadFonts() {
  try {
    await document.fonts.load('16px "Roboto Condensed"').catch(error => {
      console.warn('Error loading the bundled Roboto Condensed font:', error);
    });

    // Load OpenHASP icon font - ensure it's fully loaded before rendering
    const openHaspFont = new FontFace('Material Icons', 'url(/fonts/openhasp-icons.ttf)');
    await openHaspFont.load();
    document.fonts.add(openHaspFont);
    // IconPicker uses the explicit OpenHASP family declared in CSS, backed by
    // the very same TTF.  Trigger its load before the first modal opens.
    await document.fonts.load('16px "OpenHASP Built-in Icons"');
    
    // Load Font Awesome font
    const faFont = new FontFace('Font Awesome Icons', 'url(/fonts/FontAwesome5-Solid+Brands+Regular.woff)');
    await faFont.load();
    document.fonts.add(faFont);
    
    
    // Wait a bit more to ensure fonts are in the cache
    await new Promise(resolve => setTimeout(resolve, 100));
  } catch (error) {
    console.warn('Error loading icon fonts:', error);
  }
}

/* add icons to the library */
library.add(faUserSecret, faPlusCircle, faCircle, faTrashCan, faEdit, faHouse, faEye, faEyeSlash, faBolt, faExpand, faCompress, faFont, faImage, faDatabase, faVolumeHigh, faWaveSquare, faRotateLeft, faCube);

// import './components';
const app = createApp(App);
app.use(createPinia());

// Register Phase 1 resource adapters (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
// Nothing consumes the registry yet — this only makes the dataset adapter
// available for later phases to build on.
registerAdapter('dataset', datasetResourceAdapter)
registerAdapter('font', fontResourceAdapter)
registerAdapter('image', imageResourceAdapter)
registerAdapter('audio', audioResourceAdapter)
registerAdapter('model3d', model3dResourceAdapter)
app.component("form-button", Button);
app.component("form-input", Input);
app.component("form-textarea", Textarea);
app.component("form-label", Label);
app.component("form-Position", Position);
app.component("form-select", Select);
app.component("form-checkbox", Checkbox);
app.component("form-group", FormGroup);
app.component("form-progressbar", ProgressBar);
app.component("form-tabcontainer", TabContainer);
app.component("form-tabpane", TabPane);
app.component("color-picker", ColorPicker);
app.component('font-awesome-icon', FontAwesomeIcon);
app.component('tabs', Tabs);
app.component('tab', Tab);

trackVisit()

// Dev-only: validate Schema V2 consistency before any widget is
// instantiated (duplicate wire keys, missing exportSuffix, orphaned
// theme/defaults properties, invalid metadata, etc). Read-only — never
// modifies a schema. See src/Hasp/properties/v2/schemaValidator.js and
// `npm run schema:validate` for a standalone CLI report.
if (import.meta.env.DEV) {
  import('./Hasp/properties/v2/schemaValidator.js').then(({ validateAllSchemas, formatFindings }) => {
    Promise.all([
      import('./Hasp/properties/v2/index.js'),
      import('./Hasp/properties/themes/theme.js'),
    ]).then(([{ objectSchemaV2 }, { getTheme }]) => {
      const THEME_NAMES = ['haspdark', 'hasplight', 'mono', 'materialdark', 'materiallight'];
      const getThemesForType = (objType) => Object.fromEntries(
        THEME_NAMES.map(name => [name, getTheme(name, '#00aaff', '#ff9966')[objType]])
      );
      const results = validateAllSchemas(objectSchemaV2, getThemesForType);
      const types = Object.keys(results);
      if (types.length === 0) return;
      const errorTypes = types.filter(t => results[t].some(f => f.severity === 'error'));
      const errorCount = types.reduce((n, t) => n + results[t].filter(f => f.severity === 'error').length, 0);
      const warningCount = types.reduce((n, t) => n + results[t].filter(f => f.severity === 'warning').length, 0);
      for (const objType of errorTypes) {
        console.error(formatFindings(objType, results[objType].filter(f => f.severity === 'error')));
      }
      console.groupCollapsed(`Schema validation: ${errorCount} error(s), ${warningCount} warning(s) — run 'npm run schema:validate' for the full report`);
      for (const objType of types) console.log(formatFindings(objType, results[objType]));
      console.groupEnd();
    });
  });
}

// Preload fonts before mounting the app
preloadFonts().then(() => {
  app.mount('#app');
});
